import { type ReactNode } from "react";
import type {
  DerivedPhoto,
  InvocationPhoto,
  SuggestionRegion,
} from "../types.js";
import { ImageViewer } from "./ImageViewer.js";
import { ReadImageView } from "./ReadImageView.js";
import { ImageIcon } from "./icons.js";

/**
 * The center pane. UR-6 — the union read path no longer tiles, so the heart
 * of the pane is the per-photo READ image (`ReadImageView`): the whole-panel
 * crop the model actually read, with the geometry/column overlay. The pane
 * resolves to:
 *
 *  - a read-track photo → its read image + geometry overlay (`ReadImageView`),
 *    plus the selected breaker's box when one is picked in the reads panel;
 *  - an identity-track photo → the whole image (no pre-processing exists).
 */

export type StagePaneProps = {
  readonly selectedPhoto: InvocationPhoto | undefined;
  readonly isBreaker: boolean;
  readonly derivedPhoto: DerivedPhoto | null;
  readonly parseFailed: boolean;
  /** The selected breaker's region, highlighted on its read image. */
  readonly highlightRegion: SuggestionRegion | null;
};

export function StagePane(props: StagePaneProps) {
  const { selectedPhoto, isBreaker, parseFailed, derivedPhoto, highlightRegion } =
    props;

  if (selectedPhoto === undefined) {
    return (
      <CenterShell>
        <EmptyHint
          title="No photo to show"
          body="This invocation submitted no photos."
        />
      </CenterShell>
    );
  }

  if (parseFailed && isBreaker) {
    return (
      <CenterShell>
        <EmptyHint
          title="Nothing to show — the read never completed"
          body="The model response failed to parse, so no read image was produced and no breakers were read. Inspect the verbatim response in the right pane."
        />
      </CenterShell>
    );
  }

  if (!isBreaker) {
    return <IdentityView photo={selectedPhoto} />;
  }

  return (
    <ReadView
      photo={selectedPhoto}
      derivedPhoto={derivedPhoto}
      highlightRegion={highlightRegion}
    />
  );
}

/** Identity-track photo: the whole image, no pre-processing to walk. */
function IdentityView({ photo }: { readonly photo: InvocationPhoto }) {
  return (
    <div className="flex h-full min-h-0 flex-col bg-bg-2">
      <div className="border-b border-border-1 bg-bg-1 px-5 py-4">
        <h3 className="truncate text-base font-semibold text-fg-1" title={photo.photoId}>
          {photo.photoId}
        </h3>
      </div>
      <div className="flex min-h-0 flex-1 p-5">
        <ImageViewer src={photo.url} alt={photo.photoId} />
      </div>
    </div>
  );
}

/** Read-track photo: the per-photo read image + geometry overlay (the heart). */
function ReadView({
  photo,
  derivedPhoto,
  highlightRegion,
}: {
  readonly photo: InvocationPhoto;
  readonly derivedPhoto: DerivedPhoto | null;
  readonly highlightRegion: SuggestionRegion | null;
}) {
  const read = derivedPhoto?.read ?? null;

  return (
    <div className="flex h-full min-h-0 flex-col bg-bg-2">
      <div className="border-b border-border-1 bg-bg-1 px-5 py-4">
        <h3 className="truncate text-base font-semibold text-fg-1" title={photo.photoId}>
          {photo.photoId}
        </h3>
      </div>
      <div className="flex min-h-0 flex-1 flex-col p-5">
        {read === null ? (
          <EmptyHint
            title="No read image"
            body="This invocation produced no derived read image (it predates the union read path, or derived persistence was off). Inspect the reads on the right."
          />
        ) : (
          <ReadImageView
            read={read}
            photoId={photo.photoId}
            highlightRegion={highlightRegion}
          />
        )}
      </div>
    </div>
  );
}

function CenterShell({ children }: { readonly children: ReactNode }) {
  return (
    <div className="flex h-full min-h-0 items-center justify-center bg-bg-2 p-5">
      {children}
    </div>
  );
}

/** The reused centered empty/error hint (handoff §3 EmptyHint pattern). */
function EmptyHint({
  title,
  body,
}: {
  readonly title: string;
  readonly body: string;
}) {
  return (
    <div className="m-auto flex max-w-[440px] flex-col items-center gap-3 text-center">
      <div className="flex h-[54px] w-[54px] items-center justify-center rounded-[14px] border border-border-1 bg-bg-3 text-fg-3">
        <ImageIcon />
      </div>
      <h3 className="text-base font-semibold text-fg-1">{title}</h3>
      <p className="text-sm text-fg-2">{body}</p>
    </div>
  );
}
